import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import Loading from '../../../../../components/Loading';
import ReactSelect from '../../../../../components/ReactSelect';
import { COUNTRIES } from '../../../create/countries';
import { LANGUAGES } from '../../../create/languages';
import ErrorMessage from '../../../ErrorMessage';
import AddKeywordModal from '../../../promptGenerator/components/addKeywordModal';
import PromptsModal from '../../../promptGenerator/components/ExistingTopics/modal';
import PromptsView from '../../../promptGenerator/components/PromptsView';
import {
    generatePrompts,
    getCredits,
    getGeneratedAllTopicsApi,
    getPromptCredits,
    getPromptGenerationApi,
} from './query';
import {
    resolveCountryLanguage,
    saveCountryLanguage,
} from '../../../../../helpers/aiTrackingCountryLanguage';

const MAX_SELECTED_TOPICS = 10;

class Topics extends Component {
    constructor(props) {
        super(props);

        this.getTopics = this.getTopics.bind(this);
        this.getCreditCounts = this.getCreditCounts.bind(this);
        this.addActive = this.addActive.bind(this);
        this.onChangeTag = this.onChangeTag.bind(this);
        this.handleSelectCountry = this.handleSelectCountry.bind(this);
        this.handleSelectLanguage = this.handleSelectLanguage.bind(this);
        this.onSelectTopic = this.onSelectTopic.bind(this);
        this.handleClickTopicPrompts = this.handleClickTopicPrompts.bind(this);
        this.onGeneratePrompts = this.onGeneratePrompts.bind(this);
        this.getGeneratedPrompts = this.getGeneratedPrompts.bind(this);
        this.showOpened = this.showOpened.bind(this);
        this.onClickOnStep = this.onClickOnStep.bind(this);
        this.onSelectPrompts = this.onSelectPrompts.bind(this);
        this.onSelectAllPrompts = this.onSelectAllPrompts.bind(this);
        this.openAddKeywordModal = this.openAddKeywordModal.bind(this);
        this.closeAddKeywordModal = this.closeAddKeywordModal.bind(this);
        this.onPromptsTracked = this.onPromptsTracked.bind(this);

        const { country, language } = this.getDefaultCountryLanguage();

        this.state = {
            topics: [],
            loading: false,
            tag: 'with_prompts',
            selectedTopic: null,
            selectedTopics: [],
            topicsErrorMessage: '',
            country,
            language,
            errors: {},
            isInProgress: false,
            errorMessage: null,
            generatedTopics: [],
            selectedCollapse: ['l-0', 'r-0'],
            selectedPrompts: [],
            isAddKeywordModalOpen: false,
            promptCredits: 0,
            availableCredits: 0,
            creditsLoader: false,
        };
    }

    componentDidMount() {
        if (this.props.project?.id) {
            this.getTopics(this.props.project.id);
        }
        this.getCreditCounts();
    }

    componentDidUpdate(prevProps) {
        const projectId = this.props.project?.id;
        if (projectId && projectId !== prevProps.project?.id) {
            this.getTopics(projectId);
            // This pane mounts before the page has fetched the project, so the constructor could
            // only fall back to localStorage. Re-resolve now that the project's own defaults are
            // known -- unless the user has already picked a market by hand.
            if (!this.countryLanguageTouched) {
                this.setState(this.getDefaultCountryLanguage());
            }
        }
        if (projectId && this.props.updateAll !== prevProps.updateAll) {
            this.getTopics(projectId);
        }
    }

    componentWillUnmount() {
        if (this.abortController) this.abortController.abort();
        if (this.pollTimeout) clearTimeout(this.pollTimeout);
    }

    /**
     * Prefer the project's own defaults, then the country/language the user picked last in the
     * prompt generator, and finally the ones the project's prompts are already tracked with, so
     * generation works without asking.
     */
    getDefaultCountryLanguage() {
        const firstPrompt = this.props.project?.prompts?.[0] ?? null;

        return resolveCountryLanguage(this.props.project, {
            country: firstPrompt?.country,
            language: firstPrompt?.language,
        });
    }

    async getTopics(id) {
        try {
            if (this.abortController) this.abortController.abort();
            this.abortController = new AbortController();
            this.setState({ loading: true, topics: [] });
            const res = await getGeneratedAllTopicsApi(id, this.abortController.signal);
            const data = await res.json();
            this.setState({
                topics: Array.isArray(data) ? data : [],
                loading: false,
            });
        } catch (err) {
            if (err.name === 'AbortError') return;
            this.setState({ loading: false });
            console.log('err', err);
        }
    }

    async getCreditCounts() {
        try {
            this.setState({ creditsLoader: true });
            const [promptCreditsRes, creditsRes] = await Promise.all([
                getPromptCredits(),
                getCredits(),
            ]);
            const promptCreditsData = await promptCreditsRes.json();
            const creditsData = await creditsRes.json();
            const promptCredits =
                promptCreditsData?.allowed_per_plan +
                    promptCreditsData?.extra_allowed -
                    promptCreditsData?.total_used || 0;
            this.setState({
                promptCredits,
                availableCredits: creditsData?.available_credits ?? 0,
                creditsLoader: false,
            });
        } catch (err) {
            this.setState({ promptCredits: 0, availableCredits: 0, creditsLoader: false });
            console.log('err', err);
        }
    }

    addActive(value) {
        return this.state.tag === value;
    }

    onChangeTag(value) {
        this.setState({ tag: value });
    }

    handleSelectCountry(data) {
        this.countryLanguageTouched = true;
        this.setState({
            country: data.value,
            language: data.language,
            errors: { ...this.state.errors, country: null, language: null },
        });
    }

    handleSelectLanguage(value) {
        this.countryLanguageTouched = true;
        this.setState({ language: value, errors: { ...this.state.errors, language: null } });
    }

    onSelectTopic(topicId) {
        const newTopics = this.state.selectedTopics.includes(topicId)
            ? this.state.selectedTopics.filter((item) => item !== topicId)
            : [...this.state.selectedTopics, topicId];

        if (newTopics.length > MAX_SELECTED_TOPICS) {
            this.setState({
                topicsErrorMessage: this.props.t('ai_tracking.prompt_generator.max_topics', {
                    max: MAX_SELECTED_TOPICS,
                }),
            });
            return;
        }

        this.setState({ selectedTopics: newTopics, topicsErrorMessage: '' });
    }

    handleClickTopicPrompts(topic) {
        this.setState({
            selectedTopic: this.state.selectedTopic?.id === topic?.id ? null : topic,
        });
    }

    showOpened(name, value) {
        return this.state.selectedCollapse.includes(name) ? value ?? 'active' : '';
    }

    onClickOnStep(id) {
        this.setState({
            selectedCollapse: this.state.selectedCollapse.includes(id)
                ? this.state.selectedCollapse.filter((item) => item !== id)
                : [...this.state.selectedCollapse, id],
        });
    }

    onSelectPrompts(prompt) {
        this.setState({
            selectedPrompts: this.state.selectedPrompts.includes(prompt)
                ? this.state.selectedPrompts.filter((item) => item !== prompt)
                : [...this.state.selectedPrompts, prompt],
        });
    }

    onSelectAllPrompts(prompts) {
        const allSelected = prompts.every((prompt) => this.state.selectedPrompts.includes(prompt));
        this.setState({
            selectedPrompts: allSelected
                ? this.state.selectedPrompts.filter((item) => !prompts.includes(item))
                : [...new Set([...this.state.selectedPrompts, ...prompts])],
        });
    }

    openAddKeywordModal() {
        this.setState({ isAddKeywordModalOpen: true });
    }

    closeAddKeywordModal() {
        this.setState({ isAddKeywordModalOpen: false });
        this.onPromptsTracked();
    }

    // Tracking changes the project, so the prompts pane's "already tracked" list, the per-topic
    // counts beside the names and the credit figures are all stale until they are refetched.
    onPromptsTracked() {
        this.getCreditCounts();
        if (this.props.project?.id) this.getTopics(this.props.project.id);
        if (this.props.getProjectData) this.props.getProjectData();
    }

    async onGeneratePrompts(e) {
        try {
            e.preventDefault();
            const { t, project } = this.props;
            let isError = false;
            const errors = {};

            if (!project?.own_domain || !project?.own_product_name) {
                errors.project = t('ai_tracking.prompt_generator.brand_and_domain_required');
                isError = true;
            }
            if (!this.state.country) {
                errors.country = t('ai_tracking.prompt_generator.country_required');
                isError = true;
            }
            if (!this.state.language) {
                errors.language = t('ai_tracking.prompt_generator.language_required');
                isError = true;
            }

            if (isError) {
                return this.setState((prevState) => ({
                    errors: { ...prevState.errors, ...errors },
                }));
            }

            this.setState({
                isInProgress: true,
                errorMessage: null,
                generatedTopics: [],
                selectedPrompts: [],
            });

            const formData = {
                brand_name: project?.own_product_name,
                brand_domain: project?.own_domain,
                country: this.state.country,
                language: this.state.language,
                project: project?.id,
                topic_ids: this.state.selectedTopics,
            };

            saveCountryLanguage({
                country: this.state.country,
                language: this.state.language,
            });

            const response = await generatePrompts(formData);
            const data = await response.json();

            if (data?.status === 'completed') {
                this.onPromptsGenerated(data?.topics ?? []);
            } else if (data?.status === 'pending') {
                this.getGeneratedPrompts(data.id);
            } else {
                this.setState({
                    isInProgress: false,
                    errorMessage:
                        data?.error_message ??
                        data?.detail ??
                        t('ai_tracking.prompt_generator.failed_to_generate_prompts'),
                });
            }
        } catch (err) {
            this.setState({
                isInProgress: false,
                errorMessage: this.props.t(
                    'ai_tracking.prompt_generator.failed_to_generate_prompts',
                ),
            });
            console.log('err', err);
        }
    }

    onPromptsGenerated(topics) {
        this.setState({ generatedTopics: topics, isInProgress: false });
        this.getCreditCounts();
        if (this.props.project?.id) this.getTopics(this.props.project.id);
    }

    async getGeneratedPrompts(id) {
        try {
            const response = await getPromptGenerationApi(id);
            const data = await response.json();

            if (data?.status === 'completed') {
                this.onPromptsGenerated(data?.topics ?? []);
            } else if (data?.status === 'pending') {
                this.pollTimeout = setTimeout(() => this.getGeneratedPrompts(id), 5000);
            } else {
                this.setState({
                    isInProgress: false,
                    errorMessage:
                        data?.error_message ??
                        this.props.t('ai_tracking.prompt_generator.failed_to_generate_prompts'),
                });
            }
        } catch (err) {
            this.setState({
                isInProgress: false,
                errorMessage: this.props.t(
                    'ai_tracking.prompt_generator.failed_to_generate_prompts',
                ),
            });
            console.log('err', err);
        }
    }

    /**
     * A topic in the "With Prompts" tab. The row is what drives the prompts pane beside it, so
     * the picked one stays highlighted -- otherwise nothing on screen says which topic the
     * prompts on the right belong to once the click is over.
     */
    renderTopicName(topic) {
        const hasPrompts = topic?.generated_prompts?.length > 0;
        const isSelected = this.state.selectedTopic?.id === topic?.id;

        return (
            <div
                key={topic?.id}
                className={`gap-10 d-flex align-items-center prompt-generator-topic-row ${
                    isSelected ? 'active' : ''
                }`}
            >
                <label
                    htmlFor={`topic-${topic?.id}`}
                    onClick={() => (hasPrompts ? this.handleClickTopicPrompts(topic) : null)}
                >
                    {topic?.name}{' '}
                </label>
                {hasPrompts && (
                    <button
                        className="button-already-generated-prompts"
                        onClick={() => this.handleClickTopicPrompts(topic)}
                    >
                        (<span>{topic?.generated_prompts?.length}</span>){' '}
                    </button>
                )}
            </div>
        );
    }

    /**
     * A topic in the "Needs Prompts" tab. Laid out into side-by-side columns so a long list stays
     * compact and every name lines up with the one above it, instead of running as one long
     * column. The checkbox is nested inside the label, so the whole entry toggles the topic.
     */
    renderTopicTag(topic) {
        const isSelected = this.state.selectedTopics.includes(topic?.id);

        return (
            <label
                key={topic?.id}
                className={`prompt-generator-topic-tag ${isSelected ? 'active' : ''}`}
            >
                <input
                    id={`topic-${topic?.id}`}
                    type="checkbox"
                    onChange={() => this.onSelectTopic(topic?.id)}
                    checked={isSelected}
                />
                <span>{topic?.name}</span>
            </label>
        );
    }

    render() {
        const { t, project } = this.props;
        const alreadyTrackedPrompts =
            project?.prompts?.map((item) => item.prompt?.toLowerCase()) ?? [];

        const tabs = [
            { id: 'with_prompts', label: t('ai_tracking.prompt_generator.with_prompts') },
            { id: 'needs_prompts', label: t('ai_tracking.prompt_generator.needs_prompts') },
        ];

        const withGeneratedPrompts =
            this.state.topics?.filter((item) => item.generated_prompts?.length > 0) ?? [];
        const needsPrompts =
            this.state.topics?.filter((item) => item.generated_prompts?.length === 0) ?? [];

        return (
            <>
                <div className="card">
                    <div className="card-body">
                        <div className="prompt-generator-title">
                            <div className="prompt-generator-label">
                                {t('ai_tracking.prompt_generator.select_topics')}
                            </div>
                        </div>
                        {this.state.loading && <Loading padding="10px 0" />}
                        {!this.state.loading && !this.state.topics?.length && (
                            <div className="prompt-generator-subtitle">{t('global.no_data')}</div>
                        )}
                        {!this.state.loading && this.state.topics?.length > 0 && (
                            <>
                                <div className="tags-container">
                                    {tabs.map((tab) => (
                                        <span
                                            className={`tab-item ${
                                                this.addActive(tab.id) && 'active-tag'
                                            }`}
                                            key={tab.id}
                                            onClick={() => this.onChangeTag(tab.id)}
                                        >
                                            {tab.label}
                                        </span>
                                    ))}
                                </div>
                                <div className="prompt-generator-body-box grid-1">
                                    <div className="prompt-generator-body-box-content existing-topics">
                                        {this.state.tag === 'with_prompts' && (
                                            <>
                                                <div className="prompt-generator-step-box-body topics-list-pane">
                                                    {withGeneratedPrompts.map((topic) =>
                                                        this.renderTopicName(topic),
                                                    )}
                                                </div>
                                                {this.state.selectedTopic ? (
                                                    <div className="prompt-generator-step-box-body topics-prompts-pane">
                                                        <PromptsModal
                                                            topic={this.state.selectedTopic}
                                                            alreadyTrackedPrompts={
                                                                alreadyTrackedPrompts
                                                            }
                                                            projectId={project?.id}
                                                            onTracked={this.onPromptsTracked}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="prompt-generator-step-box-body topics-prompts-pane topics-prompts-pane--empty">
                                                        {t(
                                                            'ai_tracking.prompt_generator.select_topic_message',
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {this.state.tag === 'needs_prompts' && (
                                            <div className="prompt-generator-step-box-body w-100 prompt-generator-topic-tags">
                                                {needsPrompts.map((topic) =>
                                                    this.renderTopicTag(topic),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {this.state.topicsErrorMessage && (
                                    <ErrorMessage warning message={this.state.topicsErrorMessage} />
                                )}

                                {this.state.tag === 'needs_prompts' && (
                                    <>
                                        <div className="prompt-generator-selects-box">
                                            <div>
                                                <ReactSelect
                                                    options={COUNTRIES}
                                                    value={COUNTRIES.find(
                                                        (x) => x.value === this.state.country,
                                                    )}
                                                    label={
                                                        <div className="select-label">
                                                            {t('ai_tracking.add_keyword.country')}{' '}
                                                            <span>*</span>
                                                        </div>
                                                    }
                                                    placeholder={t(
                                                        'ai_tracking.add_keyword.country',
                                                    )}
                                                    customeOnChange={this.handleSelectCountry}
                                                    blurInputOnSelect={true}
                                                    classname="mr-0"
                                                    disabled={this.state.isInProgress}
                                                    isSearchable
                                                    components={{
                                                        IndicatorSeparator: () => null,
                                                    }}
                                                />
                                                {this.state.errors['country'] && (
                                                    <div className="error-message">
                                                        {this.state.errors['country']}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <ReactSelect
                                                    options={LANGUAGES}
                                                    value={LANGUAGES.find(
                                                        (x) => x.value === this.state.language,
                                                    )}
                                                    label={
                                                        <div className="select-label">
                                                            {t('ai_tracking.add_keyword.language')}{' '}
                                                            <span>*</span>
                                                        </div>
                                                    }
                                                    placeholder={t(
                                                        'ai_tracking.add_keyword.language',
                                                    )}
                                                    onChange={this.handleSelectLanguage}
                                                    blurInputOnSelect={true}
                                                    classname="mr-0"
                                                    disabled={this.state.isInProgress}
                                                    isSearchable
                                                    components={{
                                                        IndicatorSeparator: () => null,
                                                    }}
                                                />
                                                {this.state.errors['language'] && (
                                                    <div className="error-message">
                                                        {this.state.errors['language']}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="prompt-generator-generate-action">
                                                <button
                                                    onClick={this.onGeneratePrompts}
                                                    className="button button-filled button-primary"
                                                    disabled={
                                                        this.state.selectedTopics.length === 0 ||
                                                        this.state.promptCredits === 0 ||
                                                        this.state.isInProgress
                                                    }
                                                >
                                                    {t(
                                                        'ai_tracking.prompt_generator.generate_prompts',
                                                    )}
                                                </button>
                                                <div className="prompt-generation-count">
                                                    {this.state.creditsLoader ? (
                                                        <Loading />
                                                    ) : (
                                                        <>
                                                            {this.state.promptCredits}{' '}
                                                            {t(
                                                                'ai_tracking.global.prompts_generation_left',
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {this.state.errors['project'] && (
                                            <div className="error-message">
                                                {this.state.errors['project']}
                                            </div>
                                        )}
                                        {!this.state.creditsLoader &&
                                            this.state.promptCredits === 0 && (
                                                <div className="d-flex justify-content-end mt-3 mb-5">
                                                    <ErrorMessage
                                                        message={t(
                                                            'ai_tracking.global.prompts_generation_left_message',
                                                        )}
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {(this.state.isInProgress ||
                    this.state.errorMessage ||
                    this.state.generatedTopics.length > 0) && (
                    <div className="card">
                        <div className="card-body">
                            <div className="prompt-generator-title">
                                <div className="prompt-generator-label">
                                    {t('ai_tracking.prompt_generator.select_prompts')}
                                </div>
                            </div>
                            {this.state.isInProgress && (
                                <div className="mt-20 d-flex gap-8">
                                    <strong>
                                        {t('ai_tracking.prompt_generator.generate_message')}
                                    </strong>{' '}
                                    <Loading />
                                </div>
                            )}
                            {this.state.errorMessage && (
                                <div className="mt-20 error-message">
                                    <strong>{this.state.errorMessage}</strong>
                                </div>
                            )}
                            {this.state.generatedTopics.length > 0 && (
                                <>
                                    <div className="prompt-generator-body-box">
                                        <div className="prompt-generator-body-box-content">
                                            {this.state.generatedTopics
                                                .filter((_, i) => i % 2 === 0)
                                                .map((item, i) => (
                                                    <PromptsView
                                                        item={item}
                                                        showOpened={this.showOpened}
                                                        onClickOnStep={this.onClickOnStep}
                                                        headerKey={'name'}
                                                        promptKey="generated_prompts"
                                                        selectedItems={this.state.selectedPrompts}
                                                        onSelectPrompts={this.onSelectPrompts}
                                                        uniqueKey={`l-${i}`}
                                                        key={`l-${i}`}
                                                        onSelectAllPrompts={this.onSelectAllPrompts}
                                                        disabledPrompts={alreadyTrackedPrompts}
                                                    />
                                                ))}
                                        </div>
                                        <div className="prompt-generator-body-box-content">
                                            {this.state.generatedTopics
                                                .filter((_, i) => i % 2 === 1)
                                                .map((item, i) => (
                                                    <PromptsView
                                                        item={item}
                                                        showOpened={this.showOpened}
                                                        onClickOnStep={this.onClickOnStep}
                                                        headerKey={'name'}
                                                        promptKey="generated_prompts"
                                                        selectedItems={this.state.selectedPrompts}
                                                        onSelectPrompts={this.onSelectPrompts}
                                                        uniqueKey={`r-${i}`}
                                                        key={`r-${i}`}
                                                        onSelectAllPrompts={this.onSelectAllPrompts}
                                                        disabledPrompts={alreadyTrackedPrompts}
                                                    />
                                                ))}
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-end mt-3 mb-5">
                                        <div>
                                            <button
                                                className="button button-filled button-primary width-sm btn d-flex align-items-center justify-content-center gap-8"
                                                onClick={this.openAddKeywordModal}
                                                disabled={
                                                    this.state.selectedPrompts.length === 0 ||
                                                    this.state.availableCredits === 0
                                                }
                                            >
                                                {t(
                                                    'ai_tracking.prompt_generator.track_selected_prompts',
                                                )}
                                                <div className="selected-items-count-for-track">
                                                    {this.state.selectedPrompts.length}
                                                </div>
                                            </button>
                                            <div className="prompt-generation-count">
                                                {this.state.creditsLoader ? (
                                                    <Loading />
                                                ) : (
                                                    <>
                                                        {this.state.availableCredits}{' '}
                                                        {t(
                                                            'ai_tracking.global.prompts_tracking_left',
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {!this.state.creditsLoader &&
                                        this.state.availableCredits === 0 && (
                                            <ErrorMessage
                                                message={t(
                                                    'ai_tracking.global.prompts_tracking_left_message',
                                                )}
                                            />
                                        )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {this.state.isAddKeywordModalOpen && (
                    <AddKeywordModal
                        isOpen={this.state.isAddKeywordModalOpen}
                        onClose={this.closeAddKeywordModal}
                        prompts={this.state.selectedPrompts}
                        projectId={project?.id}
                    />
                )}
            </>
        );
    }
}

export default withTranslation()(Topics);
